// src/components/GroupEmailModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Mail, 
  Users, 
  CheckCircle, 
  XCircle, 
  Crown, 
  AlertCircle,
  Check,
  Minus,
  Star,
  FileText,
  Send,
  Eye,
  EyeOff
} from 'lucide-react';

const GroupEmailModal = ({ isOpen, onClose, candidates, onSendEmails }) => {
    const [selectedCandidates, setSelectedCandidates] = useState(new Set());
    const [emailSubject, setEmailSubject] = useState('Opportunité professionnelle - Votre candidature');
    const [emailContent, setEmailContent] = useState(`Bonjour [Prénom] [Nom],
   
   Suite à votre candidature concernant notre offre pour le poste de Chef d'équipe, votre profil a retenu notre attention.
   
   Nous avons examiné votre CV avec attention et nous sommes intéressés par votre parcours professionnel.
   
   Afin de poursuivre le processus de recrutement, nous souhaiterions connaître :
   - Vos prétentions salariales pour ce poste
   - Votre disponibilité pour un éventuel démarrage
   - Votre motivation pour rejoindre notre équipe
   
   Nous restons à votre disposition pour tout complément d'information et espérons avoir l'opportunité d'échanger avec vous prochainement.
   
   Cordialement,
   
   [Nom du recruteur]
   Responsable Ressources Humaines
   [Nom de la société]
   [Email de contact]
   [Téléphone]`);
    
    const [showPreview, setShowPreview] = useState(false);
   
    // Séparer les candidats avec email des autres
    const candidatesWithEmail = candidates.filter(c => c.email && c.email.trim() !== '');
    const candidatesWithoutEmail = candidates.filter(c => !c.email || c.email.trim() === '');
    
    const recommendedCandidates = candidatesWithEmail.filter(c => (c.score || 0) >= 70);
    const otherCandidates = candidatesWithEmail.filter(c => (c.score || 0) < 70);
   
    // Sélectionner les recommandés par défaut
    useEffect(() => {
      if (isOpen) {
        const defaultSelected = new Set(
          recommendedCandidates.map(c => c.fileName)
        );
        setSelectedCandidates(defaultSelected);
      }
    }, [isOpen, recommendedCandidates.length]); // Correction de la dépendance
   
    // Reset quand le modal se ferme
    useEffect(() => {
      if (!isOpen) {
        setSelectedCandidates(new Set());
        setShowPreview(false);
      }
    }, [isOpen]);
   
    if (!isOpen) return null;
   
    const toggleCandidate = (fileName) => {
      console.log('Toggle candidat:', fileName); // Debug
      setSelectedCandidates(prev => {
        const newSet = new Set(prev);
        if (newSet.has(fileName)) {
          newSet.delete(fileName);
          console.log('Candidat désélectionné:', fileName);
        } else {
          newSet.add(fileName);
          console.log('Candidat sélectionné:', fileName);
        }
        console.log('Nouveau set:', Array.from(newSet));
        return newSet;
      });
    };
   
    const selectAll = () => {
      const allWithEmail = candidatesWithEmail.map(c => c.fileName);
      setSelectedCandidates(new Set(allWithEmail));
      console.log('Tous sélectionnés:', allWithEmail);
    };
   
    const deselectAll = () => {
      setSelectedCandidates(new Set());
      console.log('Tous désélectionnés');
    };
   
    const selectRecommended = () => {
      const recommendedWithEmail = recommendedCandidates.map(c => c.fileName);
      setSelectedCandidates(new Set(recommendedWithEmail));
      console.log('Recommandés sélectionnés:', recommendedWithEmail);
    };
   
    const selectOthers = () => {
      const othersWithEmail = otherCandidates.map(c => c.fileName);
      setSelectedCandidates(new Set(othersWithEmail));
      console.log('Autres sélectionnés:', othersWithEmail);
    };

  const getScoreIcon = (score) => {
    if (score >= 90) return <Crown className="w-4 h-4 text-yellow-400" />;
    if (score >= 80) return <Star className="w-4 h-4 text-green-400" />;
    if (score >= 70) return <CheckCircle className="w-4 h-4 text-blue-400" />;
    return <AlertCircle className="w-4 h-4 text-gray-400" />;
  };

  const CandidateItem = ({ candidate, isRecommended }) => {
    if (!candidate.email) {
      return (
        <div className="p-4 rounded-xl border bg-gray-500/10 border-gray-500/30 opacity-50">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 rounded border-2 border-gray-500 bg-gray-600 cursor-not-allowed"></div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                {getScoreIcon(candidate.score || 0)}
                <span className="text-gray-400 font-medium">
                  {candidate.prenom} {candidate.nom}
                </span>
                <span className="text-gray-500 text-sm font-bold">
                  {candidate.score || 0}%
                </span>
              </div>
              <p className="text-gray-500 text-sm">❌ Pas d'email disponible</p>
            </div>
          </div>
        </div>
      );
    }
  
    return (
      <div 
        className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer hover:bg-white/10 ${
          selectedCandidates.has(candidate.fileName)
            ? 'bg-blue-500/20 border-blue-500/50'
            : 'bg-white/5 border-white/10 hover:border-white/20'
        }`}
        onClick={() => toggleCandidate(candidate.fileName)}
      >
        <div className="flex items-center space-x-3">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
              selectedCandidates.has(candidate.fileName)
                ? 'bg-blue-500 border-blue-500'
                : 'border-gray-400 hover:border-blue-400'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleCandidate(candidate.fileName);
            }}
          >
            {selectedCandidates.has(candidate.fileName) && (
              <Check className="w-3 h-3 text-white" />
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              {getScoreIcon(candidate.score || 0)}
              <span className="text-white font-medium">
                {candidate.prenom} {candidate.nom}
              </span>
              <span className={`text-sm font-bold ${
                (candidate.score || 0) >= 80 ? 'text-green-400' :
                (candidate.score || 0) >= 70 ? 'text-blue-400' :
                (candidate.score || 0) >= 50 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {candidate.score || 0}%
              </span>
            </div>
            <p className="text-gray-400 text-sm">✉️ {candidate.email}</p>
       </div>
     </div>
   </div>
 );
};

  const previewEmail = (candidate) => {
    let preview = emailContent;
    preview = preview.replace(/\[Nom\]/g, candidate.nom || '[Nom]');
    preview = preview.replace(/\[Prénom\]/g, candidate.prenom || '[Prénom]');
    preview = preview.replace(/\[Poste\]/g, 'Chef d\'équipe');
    return preview;
  };

  const selectedCandidatesList = candidates.filter(c => selectedCandidates.has(c.fileName));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">📧 Envoi Mail Groupé</h3>
              <p className="text-gray-400 text-sm">
                {selectedCandidates.size} candidat(s) sélectionné(s)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
            
            {/* Section sélection candidats */}
            <div className="p-6 border-r border-white/10 overflow-y-auto max-h-[70vh]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-white flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-400" />
                  👥 Sélection des Destinataires ({candidates.length} candidats)
                </h4>
              </div>

              {/* Boutons de sélection rapide */}
              <div className="grid grid-cols-2 gap-2 mb-6">
   <button
     onClick={(e) => {
       e.preventDefault();
       selectAll();
     }}
     className="flex items-center justify-center space-x-2 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-300 text-sm transition-all"
   >
     <Check className="w-4 h-4" />
     <span>Tout sélectionner</span>
   </button>
   <button
     onClick={(e) => {
       e.preventDefault();
       deselectAll();
     }}
     className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-300 text-sm transition-all"
   >
     <Minus className="w-4 h-4" />
     <span>Tout désélectionner</span>
   </button>
   <button
     onClick={(e) => {
       e.preventDefault();
       selectRecommended();
     }}
     className="flex items-center justify-center space-x-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-blue-300 text-sm transition-all"
   >
     <Star className="w-4 h-4" />
     <span>Recommandés seulement</span>
   </button>
   <button
     onClick={(e) => {
       e.preventDefault();
       selectOthers();
     }}
     className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-500/20 hover:bg-gray-500/30 border border-gray-500/30 rounded-lg text-gray-300 text-sm transition-all"
   >
     <FileText className="w-4 h-4" />
     <span>Autres seulement</span>
   </button>
 </div>

              {/* Candidats recommandés */}
              {recommendedCandidates.length > 0 && (
                <div className="mb-6">
                  <h5 className="text-green-400 font-semibold mb-3 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    ✅ Candidats Recommandés ({recommendedCandidates.length})
                  </h5>
                  <div className="space-y-2">
                    {recommendedCandidates.map(candidate => (
                      <CandidateItem 
                        key={candidate.fileName} 
                        candidate={candidate} 
                        isRecommended={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Autres candidats */}
              {otherCandidates.length > 0 && (
                <div>
                  <h5 className="text-yellow-400 font-semibold mb-3 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    ⚠️ Autres Candidats ({otherCandidates.length})
                  </h5>
                  <div className="space-y-2">
                    {otherCandidates.map(candidate => (
                      <CandidateItem 
                        key={candidate.fileName} 
                        candidate={candidate} 
                        isRecommended={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Section composition du mail */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-white flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-purple-400" />
                  ✍️ Composition du Mail Groupé
                </h4>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all"
                >
                  {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  <span>{showPreview ? 'Édition' : 'Aperçu'}</span>
                </button>
              </div>

              {!showPreview ? (
                <div className="space-y-4">
                  {/* Objet du mail */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Objet:
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Objet du mail"
                    />
                  </div>

                  {/* Contenu du mail */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Contenu (variables disponibles: [Nom], [Prénom], [Poste]):
                    </label>
                    <textarea
                      value={emailContent}
                      onChange={(e) => setEmailContent(e.target.value)}
                      rows={12}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Contenu du mail..."
                    />
                  </div>

                  {/* Variables automatiques */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <h6 className="text-blue-300 font-medium mb-2">💡 Variables automatiques</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div className="text-blue-200">
                        <strong>[Nom]</strong> → Nom du candidat
                      </div>
                      <div className="text-blue-200">
                        <strong>[Prénom]</strong> → Prénom du candidat
                      </div>
                      <div className="text-blue-200">
                        <strong>[Poste]</strong> → Chef d'équipe
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Aperçu du mail
                <div className="space-y-4">
                  <div className="p-4 bg-white/10 rounded-lg">
                    <h6 className="text-white font-medium mb-2">Aperçu pour le premier candidat sélectionné:</h6>
                    {selectedCandidatesList.length > 0 ? (
                      <div className="bg-white/5 p-4 rounded-lg">
                        <div className="text-gray-300 text-sm mb-2">
                          <strong>Objet:</strong> {emailSubject}
                        </div>
                        <div className="text-gray-300 text-sm whitespace-pre-line">
                          {previewEmail(selectedCandidatesList[0])}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">Aucun candidat sélectionné</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer avec boutons d'action */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-gray-400 text-sm">
            {selectedCandidates.size} mail(s) à envoyer
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white transition-all"
            >
              Annuler
            </button>
            <button
              onClick={() => onSendEmails({
                selectedCandidates: selectedCandidatesList,
                subject: emailSubject,
                content: emailContent
              })}
              disabled={selectedCandidates.size === 0}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
            >
              <Send className="w-4 h-4" />
              <span>Envoyer les mails</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupEmailModal;